import React, { useEffect, useState } from "react";
import SignInComponent from "./signInComponent";
import { Auth0Service, BusinessUser } from "../../services";
import {
  errorCode,
  failureMessageConstant,
  keyConstants,
  path
} from "../../constants";
import { useDispatch, useSelector } from "react-redux";
import { firstTimeLoggedIn, updateUser } from "../../slices/userSlice";
import history from "../../managers/history";
import Utils from "../../utility";
import UserService from "../../services/businessUser";
import { useLocation } from "react-router-dom";
import PropTypes from "prop-types";
const { LOGIN_THROUGH, TOKEN, IS_REFRESH_TOKEN_LOADING } = keyConstants;

const SignIn = () => {
  const dispatch = useDispatch();
  const user = useSelector((state) => state.user);
  const location = useLocation();
  const auth0Service = new Auth0Service();
  const [states, setStates] = useState({
    isLoading: { type: "", value: false },
    isPasswordEmailValid: ""
  });

  const updateState = (name, value) => {
    setStates((prevState) => ({ ...prevState, [name]: value }));
  };
  useEffect(() => {
    if (location?.hash?.includes(errorCode.USER_NOT_VERIFIED)) {
      history.push(path.EMAIL_VERIFICATION);
    } else {
      const searchParams = new URLSearchParams(location.hash.substring(1));
      const errorDescription = searchParams.get("error_description");
      if (
        errorDescription &&
        (errorDescription == failureMessageConstant.INVALID_LOGIN_TICKET ||
          failureMessageConstant.UNABLE_TO_CONFIGURE_VERIFICATION_PAGE)
      ) {
        Utils.failureToastMessage(
          failureMessageConstant.ENABLE_THIRD_PARTY_COOKIES_DESC,
          5
        );
      } else {
        Utils.failureToastMessage(errorDescription);
      }
    }
  }, [location]);
  useEffect(() => {
    const isFirstTimeLoggedIn = user?.isFirstTimeLoggedIn;
    if (user?.isLoggedIn) {
      dispatch(updateUser({ ...user, password: null }));
      if (isFirstTimeLoggedIn) {
        history.push("/setup-consent");
        dispatch(firstTimeLoggedIn(false));
      } else {
        if (Utils.localStorageGetItem(IS_REFRESH_TOKEN_LOADING)) {
          localStorage.setItem(IS_REFRESH_TOKEN_LOADING, false);
        }
        history.push("/consent");
      }
    }
  }, [user.isLoggedIn]);

  useEffect(() => {
    /**
     * Handles the authentication callback.
     *
     * @returns {Promise<void>} A promise that resolves when the authentication callback is handled.
     */
    const handleAuthCallBack = async () => {
      try {
        // Get the hash from the URL
        const hash = window.location.hash;

        // Check if the hash starts with "#access_token"
        if (hash?.startsWith("#access_token")) {
          // Update the state to indicate that content is being fetched
          updateState("isLoading", { type: "fetchContent", value: true });

          // Get the access token from the URL parameters
          const params = new URLSearchParams(hash);
          const accessToken = params.get("#access_token");

          // Create a new instance of BusinessUser with the access token
          const businessUser = new BusinessUser(accessToken);

          // Get the user info from Auth0Service
          const userInfo = await new Auth0Service().userInfo();

          // Destructure the user info and exclude unnecessary fields
          const { sub, given_name, email_verified, ...userData } = userInfo;

          // Get the user using the user id
          const getUser = await businessUser.getUser(sub);

          // Prepare the payload for updating the user
          const payload = {
            id: getUser?._id,
            isActive: true,
            state: getUser?.isActive
          };

          // Update the user using the payload
          await businessUser.putUser(payload);

          // Dispatch an action to update the user state
          dispatch(
            updateUser({
              ...userData,
              lastName: given_name,
              logo: getUser?.logo,
              name: getUser?.name,
              userId: getUser?.userId,
              _id: getUser?._id,
              isEmailVerified: email_verified,
              isLoggedIn: true
            })
          );

          // Update the state to indicate that content fetching is complete
          updateState("isLoading", { type: "fetchContent", value: false });

          // Redirect to the "/provided-consent" page
          history.push("/provided-consent");
        }
      } catch {
        // Handle any errors and update the state to indicate that content fetching is complete
        updateState("isLoading", { type: "fetchContent", value: false });
      }
    };

    handleAuthCallBack();
  }, []);

  // FUNCTION TO SIGN IN
  const signIn = async (data) => {
    try {
      const { email, password } = data;
      // Sign in using auth0Service
      localStorage.setItem(LOGIN_THROUGH, "sign-in");
      await auth0Service.signIn({ email, password });
      dispatch(firstTimeLoggedIn(false));
    } catch (err) {
      updateState(
        "isPasswordEmailValid",
        err?.error === "too_many_attempts"
          ? failureMessageConstant.ACCOUNT_BLOCKED_CHECK_MAIL_FOR_UNBLOCK_INSTRUCTIONS
          : failureMessageConstant.INCORRECT_EMAIL_PASSWORD
      );
    }
  };

  // FUNCTION TO CHECK IF BUSINESS USER EXISTS OR NOT
  const checkBusinessAlreadyExistsOrNot = async (data) => {
    const { email, password } = data;
    const userEmail = email.toLowerCase();
    const encryptedPassword = Utils.encryptPassword(password);
    dispatch(updateUser({ email: userEmail, password: encryptedPassword })); // we are saving password temporarily for non email verified users
    try {
      updateState("isLoading", { type: "login", value: true });
      const [error, businessResponse] = await Utils.parseResponse(
        new UserService().checkBusinessExists({ email: userEmail })
      );
      if (businessResponse != null) {
        const token = await new Auth0Service().getToken({ email, password });
        Utils.cookieSetItem(TOKEN, token);
        await signIn(data);
      } else {
        console.error(error);
        Utils.failureToastMessage(
          failureMessageConstant.PLEASE_CREATE_YOUR_ACCOUNT
        );
        history.push("/sign-up");
        return;
      }
    } catch (error) {
      console.error(error);
      return;
    } finally {
      updateState("isLoading", { type: "login", value: false });
    }
  };

  return (
    <SignInComponent
      states={states}
      functionObj={{ checkBusinessAlreadyExistsOrNot }}
    />
  );
};
SignIn.propTypes = {
  setDefaultWidgetSettings: PropTypes.func.isRequired,
  selectedWidgetType: PropTypes.string.isRequired,
  setSelectedWidgetType: PropTypes.func.isRequired,
  themeColors: PropTypes.shape({
    primary: PropTypes.string,
    secondary: PropTypes.string
  }).isRequired,
  setThemeColors: PropTypes.func.isRequired,
  selectedFont: PropTypes.string.isRequired,
  setSelectedFont: PropTypes.func.isRequired,
  adUnitCode: PropTypes.string.isRequired,
  setAdUnitCode: PropTypes.func.isRequired,
  showAdWidget: PropTypes.bool.isRequired,
  setShowAdWidget: PropTypes.func.isRequired,
  isEmailMarketingEnabled: PropTypes.bool.isRequired,
  setIsEmailMarketingEnabled: PropTypes.func.isRequired
};
export default SignIn;
